import { Link } from 'gatsby'
import React, { useCallback, useState } from 'react'
import Footer from '../components/footer'
import HeaderMobile from '../components/header-mobile'
import HeroLogo from '../components/hero-logo'
import Layout from '../components/layout'
import { Navs } from '../components/navs'
import withImageLightbox from '../components/HOC/with-light-box'
import LeftArrow from '../../static/svg/left-arrow.svg'

const BackButton = React.forwardRef(({ children, ...props }, ref) => {
  const onClick = (e) => {
    e.preventDefault()
    window.history.back()
  }
  return (
    <a
      {...props}
      ref={ref}
      href={typeof window !== 'undefined' ? document.referrer : ''}
      onClick={onClick}
    >
      {children}
    </a>
  )
})
const email = 'westviewparts@gmail.com'

function Product({ pageContext, lightbox }) {
  const { product } = pageContext
  const productImage = product?.image?.file?.url
  const description = product?.description?.description?.split('\n\n')
  const relatedImages =
    product?.relatedImages
      ?.map((item) => item?.file?.url)
      .filter((item) => !!item) || []
  relatedImages.unshift(productImage)
  const [productImageShowing, onSetProductImage] = useState(productImage)

  const onSelectImg = useCallback(
    (idx) => {
      const nextImg = relatedImages[idx]
      onSetProductImage(nextImg)
    },
    [onSetProductImage, relatedImages]
  )

  /*
   * Open the full-size viewer on whichever photo is currently shown. Paging
   * through inside the lightbox keeps the page's own selection in step, so
   * closing it leaves the visitor looking at the photo they stopped on rather
   * than snapping back.
   */
  const openLightbox = useCallback(() => {
    const currentIndex = Math.max(relatedImages.indexOf(productImageShowing), 0)
    lightbox.show({
      images: relatedImages,
      photoIndex: currentIndex,
      enableZoom: true,
      onIndexChange: (idx) => onSetProductImage(relatedImages[idx]),
    })
  }, [lightbox, relatedImages, productImageShowing, onSetProductImage])

  const onImageKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        openLightbox()
      }
    },
    [openLightbox]
  )

  const handleContactClick = () => {
    const subject = `Inquiry about: ${product.title || product.name}`;
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
    window.location.href = mailtoLink;
  };

  return (
    <Layout
      title='Westview Golf Cars | For Sale'
      description='We sell new and refurbished gas, electric, and custom golf cars. If you’re looking for a custom golf car, get in touch and we’ll build something you love.'
    >
      <HeaderMobile />
      <div className="container px-4">
        {/* header */}
        <div>
          <div className="px-4">
            <div className="feedback">
              <a href='tel:2695555555'>
                <div className="feedback__block">
                  <div className="feedback__block-phone-img"></div>
                  <div className="feedback__block-phone-number">
                    269 - 720 - 7168
                  </div>
                </div>
              </a>
            </div>
          </div>
          <div className="px-4">
            <div className="flex justify-center md:justify-between mt-6 md:mt-0">
              <div>
                <HeroLogo />
              </div>
              <ul className="hidden md:flex md:flex-wrap w-full pl-10 justify-around items-center">
                {Navs.map(({ text, to }, idx) => (
                  <li key={`${text}-${idx}`}>
                    <Link
                      to={to}
                      className="uppercase pl-4 md:pl-8 inline-block h-10 font-semibold text-lg"
                      key={`${to}-${idx}`}
                    >
                      {text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {/* content */}
        <div className="flex flex-col lg:flex-row p-4">
          <div className="lg:w-2/5 lg:px-4">
            <img
              src={productImageShowing}
              alt={`${product.title || product.name} — click to enlarge`}
              className="mb-4 mx-auto lg:mx-0 w-full lg:max-w-sm"
              // Tailwind 1.4 has no cursor-zoom-in utility, so set it directly.
              style={{ cursor: 'zoom-in' }}
              onClick={openLightbox}
              onKeyDown={onImageKeyDown}
              role="button"
              tabIndex="0"
            />
            {relatedImages.length > 0 && (
              <div className="flex mt-2 my-2 lg:my-0 justify-center lg:justify-start">
                {relatedImages.map((url, idx) => {
                  function select() {
                    onSelectImg(idx)
                  }
                  const selected = url === productImageShowing

                  return (
                    <img
                      key={`img-${idx}`}
                      src={url}
                      className={`w-16 h-16 mr-1 cursor-pointer rounded${
                        selected ? ' border-2 border-blue-500' : ''
                      }`}
                      onClick={select}
                      onKeyPress={select}
                      role="button"
                      tabIndex="0"
                      alt={`${product.title || product.name} — view ${idx + 1} of ${
                        relatedImages.length
                      }`}
                    />
                  )
                })}
              </div>
            )}
            <BackButton>
              <div
                role="button"
                className="mt-2 lg:mt-4 pl-4 text-xl py-1 rounded font-semibold w-48 mb-4 flex items-center bg-primary inline"
              >
                <LeftArrow className="inline-block" />
                <span className="ml-2">Go back</span>
              </div>
            </BackButton>
          </div>
          <div className="lg:w-3/5 lg:p-4 pt-0 mb-16 lg:mb-0">
            <p className="bg-primary text-xl p-2 text-center font-semibold">
              {product.title || product.name}
            </p>
            <p className="text-lg text-center my-4">
              {Number(product.price).toLocaleString('en-US', {
                currency: 'USD',
                style: 'currency',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
            </p>
            {description.map((d, idx) => (
              <div key={`description-${idx}`}>
                <div style={{whiteSpace: 'pre-wrap'}}>{d}</div>
                <br />
              </div>
            ))}
            <div
              role="button"
              className="mx-auto text-xl py-1 rounded font-semibold w-40 text-center bg-primary"
              onClick={handleContactClick}
              onKeyPress={handleContactClick}
              tabIndex="0"
            >
              Contact
            </div>
          </div>
        </div>
      </div>
      <Footer className="md:fixed left-0 bottom-0" />
    </Layout>
  )
}

export default withImageLightbox(Product)
